import { useEffect, useState } from 'react';
import { Newspaper, ExternalLink, RefreshCw } from 'lucide-react';
import { proxyImageUrl } from '../utils/imageProxy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTimeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const NewsTab = () => {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [sources, setSources] = useState([]);
  const [activeSource, setActiveSource] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async (sourceId = 'all') => {
    setLoading(true);
    setError('');
    try {
      const qs = sourceId && sourceId !== 'all' ? `?limit=30&source=${encodeURIComponent(sourceId)}` : '?limit=30';
      const resp = await fetch(`${API_URL}/api/news${qs}`);
      if (!resp.ok) throw new Error('Could not load news');
      const data = await resp.json();
      setArticles(data.articles || []);
      setFeatured(data.featured || []);
      if (!sources.length) setSources(data.sources || []);
    } catch (e) {
      setError(e.message || 'Could not load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load('all'); /* eslint-disable-next-line */ }, []);

  const pickSource = (sid) => {
    setActiveSource(sid);
    load(sid);
  };

  return (
    <section className="px-4 pt-4 pb-6 space-y-5" data-testid="public-news-tab">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Newspaper className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-sm font-black text-white uppercase tracking-wider">Football News</h2>
            <p className="text-[10px] text-zinc-500">Headlines from the UK's biggest sport newsrooms</p>
          </div>
        </div>
        <button
          onClick={() => load(activeSource)}
          disabled={loading}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50"
          aria-label="Refresh news"
          data-testid="news-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-zinc-300 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {/* Source filter */}
      {sources.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="news-source-filter">
          <button
            onClick={() => pickSource('all')}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${activeSource === 'all' ? 'bg-emerald-500 text-white' : 'bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10'}`}
            data-testid="news-source-all"
          >
            All sources
          </button>
          {sources.map((s) => (
            <button
              key={s.id}
              onClick={() => pickSource(s.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors flex items-center gap-1.5 ${activeSource === s.id ? 'text-white' : 'bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10'}`}
              style={activeSource === s.id ? { backgroundColor: s.color || '#10b981' } : {}}
              data-testid={`news-source-${s.id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: activeSource === s.id ? '#fff' : (s.color || '#10b981') }} />
              {s.name}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-300" data-testid="news-error">
          {error}
        </div>
      )}

      {loading && articles.length === 0 ? (
        <div className="space-y-3">
          <div className="aspect-[16/9] bg-zinc-900/60 border border-white/10 rounded-2xl animate-pulse" />
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-zinc-900/60 border border-white/10 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Featured hero — first article with an image */}
          {featured.length > 0 && activeSource === 'all' && (
            <a
              href={featured[0].url}
              target="_blank"
              rel="noreferrer noopener"
              className="block relative aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 group"
              data-testid={`news-featured-${featured[0].id}`}
            >
              <img
                src={proxyImageUrl(featured[0].image_url)}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <span
                  className="inline-block text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full text-white mb-2"
                  style={{ backgroundColor: featured[0].source_color || '#10b981' }}
                >
                  {featured[0].source_name}
                </span>
                <h3 className="text-white font-black text-base leading-snug line-clamp-2">
                  {featured[0].title}
                </h3>
                <div className="text-[11px] text-zinc-400 mt-1">{fmtTimeAgo(featured[0].published_at)}</div>
              </div>
            </a>
          )}

          {/* Article list */}
          {articles.length === 0 ? (
            <p className="text-sm text-zinc-500 py-10 text-center" data-testid="news-empty">No headlines right now — pull to refresh.</p>
          ) : (
            <div className="space-y-2" data-testid="news-list">
              {articles
                .filter((a) => activeSource !== 'all' || a.id !== featured[0]?.id)
                .map((a) => (
                  <a
                    key={a.id}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex gap-3 bg-zinc-900/60 hover:bg-zinc-900 border border-white/10 hover:border-emerald-500/30 rounded-xl p-3 transition-all group"
                    data-testid={`news-item-${a.id}`}
                  >
                    {a.image_url ? (
                      <img
                        src={proxyImageUrl(a.image_url)}
                        alt=""
                        loading="lazy"
                        className="w-20 h-20 rounded-lg object-cover bg-zinc-950 shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-zinc-950 border border-white/5 flex items-center justify-center shrink-0">
                        <Newspaper className="w-6 h-6 text-zinc-700" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <span
                          className="inline-block text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded text-white mb-1"
                          style={{ backgroundColor: a.source_color || '#10b981' }}
                        >
                          {a.source_name}
                        </span>
                        <h4 className="text-sm font-bold text-white leading-snug line-clamp-2 group-hover:text-emerald-300 transition-colors">
                          {a.title}
                        </h4>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-1.5">
                        <span>{fmtTimeAgo(a.published_at)}</span>
                        <ExternalLink className="w-3 h-3 group-hover:text-emerald-400" />
                      </div>
                    </div>
                  </a>
                ))}
            </div>
          )}

          <p className="text-[10px] text-zinc-600 text-center pt-2">
            Headlines link out to the source. SmartKick does not host the article body.
          </p>
        </>
      )}
    </section>
  );
};

export default NewsTab;
